import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import medicoVfpService, { PacienteVfp, PesoTallaResponse, ClasificacionValor, PesoTallaReferencia, PesoIdmReferencia, PesoIdhReferencia } from '../../services/medicoVfpService';
import {
  Users as UserGroupIcon,
  Pencil as PencilIcon,
  Trash2 as Trash2Icon,
  FileText as DocumentTextIcon,
  Scale as ScaleIcon,
  Plus as PlusIcon,
  X as CloseIcon,
  Printer as PrinterIcon,
  ChevronDown as ChevronDownIcon,
  TrendingUp as TrendingUpIcon,
} from 'lucide-react';
import PercentilModal from '../../components/PercentilModal';

const formatNacimiento = (valor: Date | string | null | undefined) => {
  if (!valor) return '-';
  const date = new Date(valor);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });
};

interface PesoTallaTablaModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  data: PesoTallaReferencia[] | null;
}

function PesoTallaTablaModal({ open, onClose, loading, data }: PesoTallaTablaModalProps) {
  if (!open) {
    return null;
  }

  const formatSexo = (sexo?: string | null) => {
    if (!sexo) return 'Sin especificar';
    const normalized = sexo.toUpperCase();
    if (normalized === 'H') return 'Hombre';
    if (normalized === 'M') return 'Mujer';
    return normalized;
  };

  const mostrarValor = (valor: number | null | undefined) => {
    if (valor === null || valor === undefined) {
      return '—';
    }
    return Number(valor).toFixed(2);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-5xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Tabla de referencia · Peso y talla infantil</h2>
            <p className="text-sm text-gray-600">Fuente: tabla `medico_vfp_peso_ni`</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600" />
            </div>
          ) : data && data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Sexo</th>
                    <th className="px-3 py-2 text-left">Años</th>
                    <th className="px-3 py-2 text-left">Meses</th>
                    <th className="px-3 py-2 text-left">Peso baja</th>
                    <th className="px-3 py-2 text-left">Peso promedio</th>
                    <th className="px-3 py-2 text-left">Peso alta</th>
                    <th className="px-3 py-2 text-left">Talla baja</th>
                    <th className="px-3 py-2 text-left">Talla promedio</th>
                    <th className="px-3 py-2 text-left">Talla alta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                  {data.map((registro) => (
                    <tr key={registro.id}>
                      <td className="px-3 py-2">{formatSexo(registro.sexo)}</td>
                      <td className="px-3 py-2">{registro.anos ?? '—'}</td>
                      <td className="px-3 py-2">{registro.meses ?? '—'}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.pesoB)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.pesoP)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.pesoA)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.tallaB)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.tallaP)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.tallaA)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-600">No se encontraron registros en la tabla de referencia.</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface PesoIdmTablaModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  data: PesoIdmReferencia[] | null;
}

function PesoIdmTablaModal({ open, onClose, loading, data }: PesoIdmTablaModalProps) {
  if (!open) {
    return null;
  }

  const mostrarValor = (valor: number | null | undefined) => {
    if (valor === null || valor === undefined) {
      return '—';
    }
    return Number(valor).toFixed(2);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Tabla de referencia · Peso y talla mujeres</h2>
            <p className="text-sm text-gray-600">Fuente: tabla `medico_vfp_peso_idm`</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600" />
            </div>
          ) : data && data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Talla</th>
                    <th className="px-3 py-2 text-left">Limite Inferior</th>
                    <th className="px-3 py-2 text-left">Limite Inferior Recomen</th>
                    <th className="px-3 py-2 text-left">Peso Promedio</th>
                    <th className="px-3 py-2 text-left">Limite Superior</th>
                    <th className="px-3 py-2 text-left">Limite Superior Recomen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                  {data.map((registro) => (
                    <tr key={registro.id}>
                      <td className="px-3 py-2">{registro.id}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.talla)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.limInf)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.limInfR)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.pesoPr)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.limSup)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.limSupR)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-600">No se encontraron registros en la tabla de referencia.</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface PesoIdhTablaModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  data: PesoIdhReferencia[] | null;
}

function PesoIdhTablaModal({ open, onClose, loading, data }: PesoIdhTablaModalProps) {
  if (!open) {
    return null;
  }

  const mostrarValor = (valor: number | null | undefined) => {
    if (valor === null || valor === undefined) {
      return '—';
    }
    return Number(valor).toFixed(2);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Tabla de referencia · Peso y talla hombres</h2>
            <p className="text-sm text-gray-600">Fuente: tabla `medico_vfp_peso_idh`</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600" />
            </div>
          ) : data && data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Talla</th>
                    <th className="px-3 py-2 text-left">Esqueleto Leve 1</th>
                    <th className="px-3 py-2 text-left">Esqueleto Leve 2</th>
                    <th className="px-3 py-2 text-left">Medio 1</th>
                    <th className="px-3 py-2 text-left">Medio 2</th>
                    <th className="px-3 py-2 text-left">Pesado 1</th>
                    <th className="px-3 py-2 text-left">Pesado 2</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                  {data.map((registro) => (
                    <tr key={registro.id}>
                      <td className="px-3 py-2">{registro.id}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.talla)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.esqL1)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.esqL2)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.esqM1)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.esqM2)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.esqP1)}</td>
                      <td className="px-3 py-2">{mostrarValor(registro.esqP2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-600">No se encontraron registros en la tabla de referencia.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MedicoVfpPage() {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: {
      focusClave?: number;
      apellido1?: string;
      apellido2?: string;
      nombre?: string;
    };
  };
  const [pacientes, setPacientes] = useState<PacienteVfp[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchApellido1, setSearchApellido1] = useState('');
  const [searchApellido2, setSearchApellido2] = useState('');
  const [searchNombre, setSearchNombre] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const DEFAULT_PAGE_SIZE = 100;
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [focusedClave, setFocusedClave] = useState<number | null>(null);
  const [selectedPaciente, setSelectedPaciente] = useState<PacienteVfp | null>(null);
  const [pesoTallaModalOpen, setPesoTallaModalOpen] = useState(false);
  const [pesoTallaData, setPesoTallaData] = useState<PesoTallaResponse | null>(null);
  const [loadingPesoTalla, setLoadingPesoTalla] = useState(false);
  const [tablaReferenciaOpen, setTablaReferenciaOpen] = useState(false);
  const [tablaReferenciaData, setTablaReferenciaData] = useState<PesoTallaReferencia[] | null>(null);
  const [loadingTablaReferencia, setLoadingTablaReferencia] = useState(false);
  const [pesoIdmTablaOpen, setPesoIdmTablaOpen] = useState(false);
  const [pesoIdmTablaData, setPesoIdmTablaData] = useState<PesoIdmReferencia[] | null>(null);
  const [loadingPesoIdmTabla, setLoadingPesoIdmTabla] = useState(false);
  const [pesoIdhTablaOpen, setPesoIdhTablaOpen] = useState(false);
  const [pesoIdhTablaData, setPesoIdhTablaData] = useState<PesoIdhReferencia[] | null>(null);
  const [loadingPesoIdhTabla, setLoadingPesoIdhTabla] = useState(false);
  const [percentilModalOpen, setPercentilModalOpen] = useState(false);
  const [selectedPacientePercentil, setSelectedPacientePercentil] = useState<PacienteVfp | null>(null);
  const pageSizeOptions = [20, 50, 100, 200, 500, 1000];
  
  // Estados para edición en línea
  const [editingCell, setEditingCell] = useState<{ clave: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [openActionsClave, setOpenActionsClave] = useState<number | null>(null);
  const [procedencia, setProcedencia] = useState('');

  const handleResetFilters = () => {
    setSearchApellido1('');
    setSearchApellido2('');
    setSearchNombre('');
    setProcedencia('');
    setFocusedClave(null);
    setPage(1);
  };

  useEffect(() => {
    const navState = location.state;
    if (navState?.focusClave != null) {
      // Guardar la clave que queremos resaltar
      setFocusedClave(navState.focusClave);

      // Aplicar filtros por nombre/apellidos para que se vean en los inputs
      if (navState.apellido1 !== undefined) {
        setSearchApellido1(navState.apellido1);
      }
      if (navState.apellido2 !== undefined) {
        setSearchApellido2(navState.apellido2);
      }
      if (navState.nombre !== undefined) {
        setSearchNombre(navState.nombre);
      }

      // Ir a la primera página con pageSize por defecto
      setPage(1);

      // Cargar inmediatamente los pacientes filtrados usando esos datos
      (async () => {
        try {
          setLoading(true);
          const response = await medicoVfpService.getPacientesVfp({
            skip: 0,
            take: DEFAULT_PAGE_SIZE,
            searchApellido1: navState.apellido1 || undefined,
            searchApellido2: navState.apellido2 || undefined,
            searchNombre: navState.nombre || undefined,
          });
          setPacientes(response.data);
          setTotal(response.total);
        } catch (error) {
          console.error('Error al cargar pacientes (focusClave):', error);
          toast.error('Error al localizar paciente en la tabla');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [location.state]);

  useEffect(() => {
    loadPacientes();
  }, [page, searchApellido1, searchApellido2, searchNombre, pageSize, procedencia]);

  const loadPacientes = async () => {
    try {
      setLoading(true);
      const response = await medicoVfpService.getPacientesVfp({
        skip: (page - 1) * pageSize,
        take: pageSize,
        searchApellido1: searchApellido1 || undefined,
        searchApellido2: searchApellido2 || undefined,
        searchNombre: searchNombre || undefined,
        procedencia: procedencia || undefined,
      });
      setPacientes(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
      toast.error('Error al cargar pacientes VFP');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPesoTalla = async (paciente: PacienteVfp) => {
    setSelectedPaciente(paciente);
    setPesoTallaModalOpen(true);
    setPesoTallaData(null);
    setLoadingPesoTalla(true);
    try {
      const data = await medicoVfpService.getPacientePesoTalla(paciente.clave);
      setPesoTallaData(data);
    } catch (error: any) {
      console.error('Error al obtener referencia de peso y talla:', error);
      const message = error?.response?.data?.message ?? 'No se pudo obtener la referencia de peso y talla';
      toast.error(message);
      setPesoTallaModalOpen(false);
      setSelectedPaciente(null);
    } finally {
      setLoadingPesoTalla(false);
    }
  };

  const handleClosePesoTalla = () => {
    setPesoTallaModalOpen(false);
    setPesoTallaData(null);
    setSelectedPaciente(null);
  };

  const handleOpenTablaReferencia = async () => {
    setTablaReferenciaOpen(true);
    setLoadingTablaReferencia(true);
    try {
      const data = await medicoVfpService.getPesoTallaTabla();
      setTablaReferenciaData(data);
    } catch (error) {
      console.error('Error al obtener la tabla de referencia de peso y talla:', error);
      toast.error('No se pudo cargar la tabla de peso y talla infantil');
      setTablaReferenciaOpen(false);
      setTablaReferenciaData(null);
    } finally {
      setLoadingTablaReferencia(false);
    }
  };

  const handleCloseTablaReferencia = () => {
    setTablaReferenciaOpen(false);
  };

  const handleOpenPesoIdmTabla = async () => {
    setPesoIdmTablaOpen(true);
    setLoadingPesoIdmTabla(true);
    try {
      const data = await medicoVfpService.getPesoIdmTabla();
      setPesoIdmTablaData(data);
    } catch (error) {
      console.error('Error al obtener la tabla de referencia de peso_idm:', error);
      toast.error('No se pudo cargar la tabla de peso y talla para mujeres');
      setPesoIdmTablaOpen(false);
      setPesoIdmTablaData(null);
    } finally {
      setLoadingPesoIdmTabla(false);
    }
  };

  const handleClosePesoIdmTabla = () => {
    setPesoIdmTablaOpen(false);
  };

  const handleOpenPesoIdhTabla = async () => {
    setPesoIdhTablaOpen(true);
    setLoadingPesoIdhTabla(true);
    try {
      const data = await medicoVfpService.getPesoIdhTabla();
      setPesoIdhTablaData(data);
    } catch (error) {
      console.error('Error al obtener la tabla de referencia de peso_idh:', error);
      toast.error('No se pudo cargar la tabla de peso y talla para hombres');
      setPesoIdhTablaOpen(false);
      setPesoIdhTablaData(null);
    } finally {
      setLoadingPesoIdhTabla(false);
    }
  };

  const handleClosePesoIdhTabla = () => {
    setPesoIdhTablaOpen(false);
  };

  const handleOpenPercentil = (paciente: PacienteVfp) => {
    setSelectedPacientePercentil(paciente);
    setPercentilModalOpen(true);
  };

  const handleClosePercentil = () => {
    setPercentilModalOpen(false);
    setSelectedPacientePercentil(null);
  };

  const handleDelete = async (clave: number) => {
    if (!confirm('¿Está seguro de eliminar este paciente?')) return;

    try {
      await medicoVfpService.deletePacienteVfp(clave);
      toast.success('Paciente eliminado exitosamente');
      loadPacientes();
    } catch (error) {
      console.error('Error al eliminar paciente:', error);
      toast.error('Error al eliminar paciente');
    }
  };

  const handleCellClick = (clave: number, field: string, currentValue: any) => {
    setEditingCell({ clave, field });
    setEditValue(currentValue?.toString() || '');
  };

  const handleCellKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, clave: number, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await saveEdit(clave, field);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const saveEdit = async (clave: number, field: string) => {
    try {
      const numValue = editValue === '' ? null : parseFloat(editValue);
      
      if (editValue !== '' && isNaN(numValue!)) {
        toast.error('Valor inválido. Ingrese un número válido.');
        return;
      }

      const updateData: any = {};
      updateData[field] = numValue;

      await medicoVfpService.updatePacienteVfp(clave, updateData);
      
      // Actualizar el estado local
      setPacientes(prev => prev.map(p => 
        p.clave === clave ? { ...p, [field]: numValue } : p
      ));
      
      toast.success('Campo actualizado exitosamente');
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error al actualizar campo:', error);
      toast.error('Error al actualizar el campo');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // Callbacks estables para mantener el foco en los inputs
  const handleSearchApellido1 = useCallback((value: string) => {
    setSearchApellido1(value);
    setPage(1);
  }, []);

  const handleSearchApellido2 = useCallback((value: string) => {
    setSearchApellido2(value);
    setPage(1);
  }, []);

  const handleSearchNombre = useCallback((value: string) => {
    setSearchNombre(value);
    setPage(1);
  }, []);

  const paginationRange = useMemo(() => {
    const siblingCount = 5;
    const totalPageNumbers = siblingCount * 2 + 5;

    if (totalPages <= totalPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(page - siblingCount, 2);
    const rightSiblingIndex = Math.min(page + siblingCount, totalPages - 1);

    const showLeftDots = leftSiblingIndex > 2;
    const showRightDots = rightSiblingIndex < totalPages - 1;

    const range: (number | string)[] = [];

    range.push(1);

    if (showLeftDots) {
      range.push('left-dots');
    }

    const start = showLeftDots ? leftSiblingIndex : 2;
    const end = showRightDots ? rightSiblingIndex : totalPages - 1;

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    if (showRightDots) {
      range.push('right-dots');
    }

    range.push(totalPages);

    return range;
  }, [page, totalPages]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="w-8 h-8 text-purple-600" />
            Paciente
          </h1>
          <p className="text-gray-600 mt-1">Gestión de pacientes</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/medico-vfp/nuevo')}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-100 px-4 py-2 text-sm font-medium text-purple-700 shadow-sm transition-all duration-200 hover:bg-purple-200 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:ring-offset-2"
          >
            <PlusIcon className="h-5 w-5" />
            Paciente Nuevo
          </button>
          <button
            onClick={handleOpenTablaReferencia}
            className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-4 py-2 text-sm font-medium text-purple-700 shadow-sm transition hover:border-purple-300 hover:bg-purple-50 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            <ScaleIcon className="h-5 w-5" />
            <div className="flex flex-col items-start">
              <span>Percentiles OMS Niños</span>
              <span className="text-xs text-purple-600">Datos oficiales OMS</span>
            </div>
          </button>
          <button
            onClick={handleOpenPesoIdmTabla}
            className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2"
          >
            <ScaleIcon className="h-5 w-5" />
            <div className="flex flex-col items-start">
              <span>Peso por Talla Mujeres</span>
              <span className="text-xs text-rose-600">Tabla de referencia adultos</span>
            </div>
          </button>
          <button
            onClick={handleOpenPesoIdhTabla}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <ScaleIcon className="h-5 w-5" />
            <div className="flex flex-col items-start">
              <span>Peso por Talla Hombres</span>
              <span className="text-xs text-blue-600">Tabla de referencia adultos</span>
            </div>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 pt-4 pb-2 flex items-center gap-4 border-b border-gray-100">
          <div className="flex-1"></div>
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-1.5 text-xs font-medium text-purple-700 shadow-sm transition hover:bg-purple-100 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:ring-offset-1"
          >
            Reiniciar filtros
          </button>
          <select
            value={procedencia}
            onChange={(e) => { setProcedencia(e.target.value); setPage(1); }}
            className="rounded-full border border-purple-200 bg-white px-4 py-1.5 text-xs font-medium text-purple-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
          >
            <option value="">Todos</option>
            <option value="beneficencia">Beneficencia Española</option>
            <option value="angeles">Hospital Angeles</option>
          </select>
          <div className="flex-1 flex justify-end">
            <span className="text-xs font-medium text-purple-700 whitespace-nowrap">
              Total registros: {total}
            </span>
          </div>
        </div>
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          )}
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-purple-50 sticky top-0 z-10">
                <tr>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Clave
                  </th>
                  <th className="px-1 py-2 text-center text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Percentiles
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Apellido 1
                    <div className="mt-2">
                      <input
                        key="input-apellido1"
                        type="text"
                        placeholder="Buscar..."
                        value={searchApellido1}
                        onChange={(e) => handleSearchApellido1(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Apellido 2
                    <div className="mt-2">
                      <input
                        key="input-apellido2"
                        type="text"
                        placeholder="Buscar..."
                        value={searchApellido2}
                        onChange={(e) => handleSearchApellido2(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Nombre
                    <div className="mt-2">
                      <input
                        key="input-nombre"
                        type="text"
                        placeholder="Buscar..."
                        value={searchNombre}
                        onChange={(e) => handleSearchNombre(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Nacimiento
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Sexo
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Peso
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Talla
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Temp
                  </th>
                  <th className="px-1 py-2 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                    TA
                  </th>
                  <th className="px-1 py-2 text-right text-xs font-medium text-purple-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!loading && pacientes.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-6 py-12 text-center text-gray-600">
                      <div className="flex flex-col items-center gap-4">
                        <UserGroupIcon className="w-12 h-12 text-gray-400" />
                        <span>No se encontraron pacientes</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pacientes.map((paciente) => (
                    <tr
                      key={paciente.clave}
                      className={`hover:bg-gray-200 ${focusedClave === paciente.clave ? 'bg-gray-200' : ''}`}
                    >
                      <td className="px-1 py-3 whitespace-nowrap text-base font-medium text-gray-900">
                        {paciente.clave}
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleOpenPercentil(paciente)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md hover:from-purple-600 hover:to-blue-600 transition text-xs font-medium shadow-sm"
                          title="Ver percentiles de crecimiento"
                        >
                          <TrendingUpIcon className="w-4 h-4" />
                          Ver
                        </button>
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-base font-medium text-gray-900">
                        {paciente.apellido1 || '-'}
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-base font-medium text-gray-900">
                        {paciente.apellido2 || '-'}
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-base font-medium text-gray-900">
                        {paciente.nombre || '-'}
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-base text-gray-500">
                        {formatNacimiento(paciente.nacimiento)}
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-base text-gray-500">
                        {paciente.sexo || '-'}
                      </td>
                      <td
                        className="px-1 py-3 whitespace-nowrap text-base text-gray-500 cursor-pointer hover:bg-purple-50"
                        onClick={() => handleCellClick(paciente.clave, 'peso', paciente.peso)}
                        title="Click para editar"
                      >
                        {editingCell?.clave === paciente.clave && editingCell?.field === 'peso' ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleCellKeyDown(e, paciente.clave, 'peso')}
                            onBlur={() => saveEdit(paciente.clave, 'peso')}
                            autoFocus
                            className="w-full px-1 py-0.5 border border-purple-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        ) : (
                          paciente.peso ? `${paciente.peso}` : '-'
                        )}
                      </td>
                      <td
                        className="px-1 py-3 whitespace-nowrap text-base text-gray-500 cursor-pointer hover:bg-purple-50"
                        onClick={() => handleCellClick(paciente.clave, 'talla', paciente.talla)}
                        title="Click para editar"
                      >
                        {editingCell?.clave === paciente.clave && editingCell?.field === 'talla' ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleCellKeyDown(e, paciente.clave, 'talla')}
                            onBlur={() => saveEdit(paciente.clave, 'talla')}
                            autoFocus
                            className="w-full px-1 py-0.5 border border-purple-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        ) : (
                          paciente.talla ? `${paciente.talla}` : '-'
                        )}
                      </td>
                      <td
                        className="px-1 py-3 whitespace-nowrap text-base text-gray-500 cursor-pointer hover:bg-purple-50"
                        onClick={() => handleCellClick(paciente.clave, 'temp', paciente.temp)}
                        title="Click para editar"
                      >
                        {editingCell?.clave === paciente.clave && editingCell?.field === 'temp' ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleCellKeyDown(e, paciente.clave, 'temp')}
                            onBlur={() => saveEdit(paciente.clave, 'temp')}
                            autoFocus
                            className="w-full px-1 py-0.5 border border-purple-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        ) : (
                          paciente.temp ? `${paciente.temp}` : '-'
                        )}
                      </td>
                      <td
                        className="px-1 py-3 whitespace-nowrap text-base text-gray-500 cursor-pointer hover:bg-purple-50"
                        onClick={() => handleCellClick(paciente.clave, 'ta', paciente.ta)}
                        title="Click para editar"
                      >
                        {editingCell?.clave === paciente.clave && editingCell?.field === 'ta' ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleCellKeyDown(e, paciente.clave, 'ta')}
                            onBlur={() => saveEdit(paciente.clave, 'ta')}
                            autoFocus
                            className="w-full px-1 py-0.5 border border-purple-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        ) : (
                          paciente.ta ? `${paciente.ta}` : '-'
                        )}
                      </td>
                      <td className="px-1 py-3 whitespace-nowrap text-right text-base font-medium">
                        <div className="relative inline-block text-left">
                          <button
                            type="button"
                            onClick={() =>
                              setOpenActionsClave((prev) =>
                                prev === paciente.clave ? null : paciente.clave,
                              )
                            }
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-purple-500"
                          >
                            Acciones
                            <ChevronDownIcon className="w-4 h-4" />
                          </button>
                          {openActionsClave === paciente.clave && (
                            <div className="absolute right-0 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                              <div className="py-1 text-sm text-gray-700">
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleOpenPesoTalla(paciente);
                                    setOpenActionsClave(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-1 hover:bg-gray-50 text-left"
                                >
                                  <ScaleIcon className="w-4 h-4 text-purple-600" />
                                  <span>Peso y talla</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/medico-vfp/${paciente.clave}/receta`);
                                    setOpenActionsClave(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-1 hover:bg-gray-50 text-left"
                                >
                                  <PrinterIcon className="w-4 h-4 text-amber-600" />
                                  <span>Receta</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/medico-vfp/${paciente.clave}/historial`);
                                    setOpenActionsClave(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-1 hover:bg-gray-50 text-left"
                                >
                                  <DocumentTextIcon className="w-4 h-4 text-green-600" />
                                  <span>Ver historial</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/medico-vfp/${paciente.clave}/editar`);
                                    setOpenActionsClave(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-1 hover:bg-gray-50 text-left"
                                >
                                  <PencilIcon className="w-4 h-4 text-blue-600" />
                                  <span>Editar</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDelete(paciente.clave);
                                    setOpenActionsClave(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-1 hover:bg-gray-50 text-left"
                                >
                                  <Trash2Icon className="w-4 h-4 text-red-600" />
                                  <span>Eliminar</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{Math.min((page - 1) * pageSize + 1, total)}</span> a{' '}
                      <span className="font-medium">{Math.min(page * pageSize, total)}</span> de{' '}
                      <span className="font-medium">{total}</span> resultados
                    </p>
                    <div className="flex items-center gap-2">
                      <label htmlFor="page-size" className="text-sm text-gray-600">
                        Tamaño de página:
                      </label>
                      <select
                        id="page-size"
                        value={pageSize}
                        onChange={(event) => {
                          const newSize = Number(event.target.value);
                          setPageSize(newSize);
                          setPage(1);
                        }}
                        className="select select-bordered select-compact"
                      >
                        {pageSizeOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      {paginationRange.map((item, idx) =>
                        item === 'left-dots' || item === 'right-dots' ? (
                          <span
                            key={`${item}-${idx}`}
                            className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-400"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => setPage(Number(item))}
                            className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                              page === item
                                ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {item}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>

      <PesoTallaModal
        open={pesoTallaModalOpen}
        onClose={handleClosePesoTalla}
        paciente={selectedPaciente}
        data={pesoTallaData}
        loading={loadingPesoTalla}
      />
      <PesoTallaTablaModal
        open={tablaReferenciaOpen}
        onClose={handleCloseTablaReferencia}
        loading={loadingTablaReferencia}
        data={tablaReferenciaData}
      />
      <PesoIdmTablaModal
        open={pesoIdmTablaOpen}
        onClose={handleClosePesoIdmTabla}
        loading={loadingPesoIdmTabla}
        data={pesoIdmTablaData}
      />
      <PesoIdhTablaModal
        open={pesoIdhTablaOpen}
        onClose={handleClosePesoIdhTabla}
        loading={loadingPesoIdhTabla}
        data={pesoIdhTablaData}
      />
      <PercentilModal
        open={percentilModalOpen}
        onClose={handleClosePercentil}
        paciente={selectedPacientePercentil}
      />
    </div>
  );
}

interface PesoTallaModalProps {
  open: boolean;
  onClose: () => void;
  paciente: PacienteVfp | null;
  data: PesoTallaResponse | null;
  loading: boolean;
}

function PesoTallaModal({ open, onClose, paciente, data, loading }: PesoTallaModalProps) {
  if (!open || !paciente) {
    return null;
  }

  const comparacion = data?.comparacion ?? null;
  const referencia = data?.referencia ?? null;
  const missingFields = data?.missingFields ?? [];
  const edadActual = data?.edadActual ?? null;
  const parametros = data?.parametrosBusqueda ?? { anos: null, meses: null };

  const formatEdad = () => {
    if (edadActual) {
      return `${edadActual.anos} años ${edadActual.meses} meses`;
    }

    if (paciente.nacimiento) {
      const nacimiento = new Date(paciente.nacimiento);
      return nacimiento.toLocaleDateString('es-MX');
    }

    return 'No disponible';
  };

  const formatSexo = (sexo?: string | null) => {
    if (!sexo) return 'No registrado';
    return sexo === 'H' ? 'Hombre' : sexo === 'M' ? 'Mujer' : sexo;
  };

  const getBadgeStyles = (estado: ClasificacionValor | null | undefined) => {
    switch (estado) {
      case 'muy_alto':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'alto':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'normal':
        return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'normal_bajo':
        return 'bg-cyan-100 text-cyan-700 border border-cyan-200';
      case 'bajo':
        return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'muy_bajo':
        return 'bg-orange-100 text-orange-700 border border-orange-200';
      case 'fuera_de_rango':
        return 'bg-red-100 text-red-700 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border border-gray-200';
    }
  };

  const getClasificacionTexto = (label: string, estado: ClasificacionValor | null | undefined) => {
    switch (estado) {
      case 'muy_alto':
        return `${label} muy alto (> P97)`;
      case 'alto':
        return `${label} alto (P85-P97)`;
      case 'normal':
        return `${label} normal (P50-P85)`;
      case 'normal_bajo':
        return `${label} normal bajo (P15-P50)`;
      case 'bajo':
        return `${label} bajo (P3-P15)`;
      case 'muy_bajo':
        return `${label} muy bajo (< P3)`;
      case 'fuera_de_rango':
        return `${label} fuera de rango`;
      default:
        return `${label} sin referencia`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Análisis de Peso y Talla</h2>
            <p className="text-sm text-gray-600">
              {paciente.apellido1} {paciente.apellido2} {paciente.nombre} · Clave {paciente.clave}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {missingFields.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h3 className="text-sm font-semibold text-amber-800">Campos faltantes</h3>
                  <p className="mt-1 text-sm text-amber-700">
                    No se pudo completar la búsqueda debido a información faltante. Por favor complete los siguientes campos:
                  </p>
                  <ul className="mt-2 list-inside list-disc text-sm text-amber-800">
                    {missingFields.map((campo) => (
                      <li key={campo}>{campo}</li>
                    ))}
                  </ul>
                </div>
              )}

              <section className="grid gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2">
                <div>
                  <span className="text-sm font-medium text-gray-600">Sexo del paciente</span>
                  <p className="text-lg font-semibold text-gray-900">{formatSexo(paciente.sexo)}</p>
                  {referencia && (
                    <p className="text-xs text-gray-500">
                      Referencia: {formatSexo(referencia.sexo)}
                      {comparacion && (
                        <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs ${comparacion.sexoCoincide ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {comparacion.sexoCoincide ? 'Sexo coincidente' : 'Sexo distinto'}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Edad calculada</span>
                  <p className="text-lg font-semibold text-gray-900">{formatEdad()}</p>
                  <p className="text-xs text-gray-500">
                    Parámetros usados: {parametros.anos ?? '—'} años · {parametros.meses ?? '—'} meses
                  </p>
                </div>
              </section>

              <section className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-gray-700">Peso del paciente</h3>
                  <div
                    className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${getBadgeStyles(comparacion?.peso)}`}
                  >
                    {getClasificacionTexto('Peso', comparacion?.peso)}
                  </div>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {paciente.peso !== undefined && paciente.peso !== null ? `${paciente.peso} kg` : 'No registrado'}
                  </p>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-gray-700">Talla del paciente</h3>
                  <div
                    className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${getBadgeStyles(comparacion?.talla)}`}
                  >
                    {getClasificacionTexto('Talla', comparacion?.talla)}
                  </div>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {paciente.talla !== undefined && paciente.talla !== null ? `${paciente.talla} m` : 'No registrada'}
                  </p>
                </div>
              </section>

              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-700">Registro de referencia encontrado</h3>
                {referencia ? (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Sexo</th>
                          <th className="px-3 py-2 text-left">Años</th>
                          <th className="px-3 py-2 text-left">Meses</th>
                          <th className="px-3 py-2 text-left">Peso baja</th>
                          <th className="px-3 py-2 text-left">Peso promedio</th>
                          <th className="px-3 py-2 text-left">Peso alta</th>
                          <th className="px-3 py-2 text-left">Talla baja</th>
                          <th className="px-3 py-2 text-left">Talla promedio</th>
                          <th className="px-3 py-2 text-left">Talla alta</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="text-sm text-gray-700">
                          <td className="px-3 py-2">{formatSexo(referencia.sexo)}</td>
                          <td className="px-3 py-2">{referencia.anos ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.meses ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.pesoB ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.pesoP ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.pesoA ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.tallaB ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.tallaP ?? '—'}</td>
                          <td className="px-3 py-2">{referencia.tallaA ?? '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    No se encontró un registro de referencia que coincida con los parámetros proporcionados.
                  </p>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
